/* RESPONSIBLE TEAM: team-frontend-tech */
/* === ⚠️ THIS FILE CURRENTLY USES DEPRECATED PATTERNS ⚠️ === */
/* === 🔗 For more information visit https://go.inter.com/ember-best-practices 🔗 */
/* === 🚀 Please consider refactoring & removing some of the comments below when working on this file 🚀 */
/* eslint-disable ember/require-computed-property-dependencies */
import { attr } from '@ember-data/model';
import { computed } from '@ember/object';
import { and, equal, map, match, none, not, notEmpty, or, readOnly } from '@ember/object/computed';
import { inject as service } from '@ember/service';
import { camelize, dasherize } from '@ember/string';
import { typeOf } from '@ember/utils';
import Fragment from 'ember-data-model-fragments/fragment';
import { applyFunction, findByProperty, notImplemented, ternaryToProperty } from '@intercom/pulse/lib/computed-properties';
import FiltersToText from 'embercom/lib/filters-to-text';
import ConversationRoutingPredicateComparisonOptions from 'predicates/models/data/conversation-routing-predicate-comparison-options';
import DefaultPredicateComparisonOptions from 'predicates/models/data/default-predicate-comparison-options';
import GoalPredicateComparisonOptions from 'predicates/models/data/goal-predicate-comparison-options';
import StatSystemGoalPredicateComparisonOptions from 'predicates/models/data/stat-system-goal-predicate-comparision-options';
import VamPredicateComparisonOptions from 'predicates/models/data/vam-predicate-comparison-options';
import PageTargetPredicateComparisonOptions from 'predicates/models/data/page-target-predicate-comparison-options';
import InboxViewsPredicateComparisonOptions from 'predicates/models/data/inbox-views-predicate-comparison-options';
import RulesPredicateComparisonOptions from 'predicates/models/data/rules-predicate-comparison-options';
import RulesFiltersPredicateComparisonOptions from 'predicates/models/data/rules-filters-predicate-comparison-options';
import ReportingPredicateComparisonOptions from 'predicates/models/data/reporting-predicate-comparison-options';
import RulesetDateFiltersPredicateComparisonOptions from 'predicates/models/data/ruleset-date-filters-predicate-comparison-options';
import { fragmentOwner } from 'ember-data-model-fragments/attributes';
import Predicate from 'predicates/models/predicate';
import { alias } from '@ember/object/computed';
import FinSdrPredicateComparisonOptions from 'predicates/models/data/fin-sdr-predicate-comparison-options';
const NULL_VALUE_COMPARISONS = ['known', 'unknown', 'increase', 'decrease', 'triggered', 'true', 'false'];

/**
 * Static map of backend operator values to full translation keys.
 * Maps operators with underscores to full translation keys with hyphens.
 */
const OPERATOR_TO_TRANSLATION_KEY = {
  eq: 'common.predicates.comparison.eq',
  ne: 'common.predicates.comparison.ne',
  in: 'common.predicates.comparison.in',
  nin: 'common.predicates.comparison.nin',
  gt: 'common.predicates.comparison.gt',
  lt: 'common.predicates.comparison.lt',
  contains: 'common.predicates.comparison.contains',
  not_contains: 'common.predicates.comparison.not-contains',
  starts_with: 'common.predicates.comparison.starts-with',
  ends_with: 'common.predicates.comparison.ends-with',
  contains_exact_word: 'common.predicates.comparison.contains-exact-word',
  known: 'common.predicates.comparison.known',
  unknown: 'common.predicates.comparison.unknown',
  true: 'common.predicates.comparison.true',
  false: 'common.predicates.comparison.false',
  increase: 'common.predicates.comparison.increase',
  decrease: 'common.predicates.comparison.decrease',
  triggered: 'common.predicates.comparison.triggered',
  matches: 'common.predicates.comparison.matches',
  // Handle operators with colons (e.g., 'lt:relative' -> 'lt')
  'lt:relative': 'common.predicates.comparison.lt',
  'gt:relative': 'common.predicates.comparison.gt',
  'eq:relative': 'common.predicates.comparison.eq',
  'lt:absolute': 'common.predicates.comparison.lt',
  'gt:absolute': 'common.predicates.comparison.gt',
  'eq:absolute': 'common.predicates.comparison.eq'
};

/**
 * Gets the translated label for an operator.
 * If the operator is in the static map, it will be translated.
 * If not, the operator name is returned as-is without translation.
 * @param {string} operator - The operator value (e.g., 'eq', 'not_contains')
 * @param {Object} intl - Intl service for translations
 * @returns {string} The translated label or the operator name if not mapped
 */
function getOperatorLabel(operator, intl) {
  let translationKey = OPERATOR_TO_TRANSLATION_KEY[operator];
  if (translationKey) {
    // Use static translation key from map
    return intl.t(translationKey, {
      default: operator
    });
  } else {
    // For unknown operators not in the map, return operator name directly
    // Do not attempt translation - just use the operator value as-is
    return operator;
  }
}

/**
 * Maps backend operator values to frontend format with labels
 * @param {Array<string>} operatorValues - Array of operator values from backend (e.g., ['eq', 'in'])
 * @param {Object} intl - Intl service for translations
 * @returns {Array<Object>} Array of comparison options with labels (e.g., [{ label: 'is', value: 'eq' }])
 */
function mapBackendOperatorsToComparisonOptions(operatorValues, intl) {
  if (!operatorValues || !Array.isArray(operatorValues) || operatorValues.length === 0) {
    return null;
  }
  return operatorValues.map(operator => {
    return {
      label: getOperatorLabel(operator, intl),
      value: operator
    };
  });
}
export default Fragment.extend({
  attributeService: service(),
  appService: service(),
  intl: service(),
  attribute: attr('string'),
  comparison: attr('string'),
  type: attr('string'),
  value: attr(),
  subclass: attr('string'),
  predicateGroup: fragmentOwner(),
  availableAttributes: computed('attributeService.allAttributes.[]', 'predicateGroup.attributeListOverride.[]', function () {
    // Had to do this tripple check because when we nest predicates we leave the attributeListOverride
    // on the root predicateGroup. This was causing nested predicates from override lists not to display correctly.
    // We only need to check 3 levels deep because that's the max we allow in our predicate builder UI.
    // https://github.com/intercom/intercom/issues/368598
    if (this.predicateGroup?.attributeListOverride) {
      return this.predicateGroup.attributeListOverride;
    } else if (this.predicateGroup?.predicateGroup?.attributeListOverride) {
      return this.predicateGroup.predicateGroup.attributeListOverride;
    } else if (this.predicateGroup?.predicateGroup?.predicateGroup?.attributeListOverride) {
      return this.predicateGroup.predicateGroup.predicateGroup.attributeListOverride;
    }
    return this.attributeService.allAttributes;
  }),
  info: findByProperty('availableAttributes', 'identifier', 'attributeIdentifier'),
  name: readOnly('info.name'),
  icon: readOnly('info.icon'),
  attributeDescription: readOnly('info.description'),
  hasAttributeOptions: readOnly('info.hasOptions'),
  attributeOptions: readOnly('info.options'),
  options: map('attributeOptions', function (option) {
    return {
      label: option.get('value'),
      value: option.get('value')
    };
  }),
  canBeGrouped: not('isClientPredicate'),
  isEditable: notImplemented('predicates/predicate model: isEditable must be implemented'),
  isNotEditable: not('isEditable'),
  hasAttribute: notEmpty('attribute'),
  hasComparison: notEmpty('comparison'),
  hasNoComparison: not('hasComparison'),
  hasType: notEmpty('type'),
  hasValue: notEmpty('value'),
  hasNoValue: not('hasValue'),
  hasAttributeComparisonAndType: and('hasAttribute', 'hasComparison', 'hasType'),
  hasAttributeValueAndType: and('hasValue', 'hasType'),
  isKnownComparison: equal('comparison', 'known'),
  isUnknownComparison: equal('comparison', 'unknown'),
  isKnownOrUnknownComparison: or('isKnownComparison', 'isUnknownComparison'),
  isIncreaseComparison: equal('comparison', 'increase'),
  isDecreaseComparison: equal('comparison', 'decrease'),
  isIncreaseOrDecreaseComparison: or('isIncreaseComparison', 'isDecreaseComparison'),
  isTriggeredComparison: equal('comparison', 'triggered'),
  isTrueComparison: equal('comparison', 'true'),
  isFalseComparison: equal('comparison', 'false'),
  isTrueOrFalseComparison: or('isTrueComparison', 'isFalseComparison'),
  isNotValueBasedComparison: or('isKnownOrUnknownComparison', 'isIncreaseOrDecreaseComparison', 'isTriggeredComparison', 'isTrueOrFalseComparison'),
  isValueBasedComparison: not('isNotValueBasedComparison'),
  valueIsNull: none('value'),
  typeOfValue: applyFunction(typeOf, 'value'),
  genericType: notImplemented('predicates/predicate model: genericType must be implemented'),
  camelizedType: applyFunction(camelize, 'type'),
  genericTypeEditor: computed('genericType', function () {
    return `${dasherize(this.genericType)}-editor`;
  }),
  attributeOptionEditor: 'select-editor',
  typeEditor: ternaryToProperty('hasAttributeOptions', 'attributeOptionEditor', 'genericTypeEditor'),
  isAnonymousType: false,
  isBooleanType: false,
  isDateType: false,
  isNumericType: false,
  isStringType: false,
  isLogicalType: false,
  isRoleType: false,
  isSelectType: false,
  isOptionalSelectType: false,
  isNumericSelectType: false,
  isUrlVisitType: false,
  isRoot: false,
  isPlanType: false,
  isLlmEvaluationType: false,
  isUserPredicate: false,
  isValid: ternaryToProperty('isNotValueBasedComparison', 'isValidForNoValue', 'isValidForValue'),
  isValidForValue: and('hasAttributeComparisonAndType', 'hasValue'),
  isValidForNoValue: and('hasAttributeComparisonAndType', 'hasNoValue'),
  isInvalid: not('isValid'),
  defaultComparison: 'eq',
  goalComparison: '',
  optionsPerMode: {
    default: DefaultPredicateComparisonOptions,
    goal: GoalPredicateComparisonOptions,
    'stat-system-goal': StatSystemGoalPredicateComparisonOptions,
    'conversation-routing': ConversationRoutingPredicateComparisonOptions,
    vam: VamPredicateComparisonOptions,
    pageTarget: PageTargetPredicateComparisonOptions,
    'inbox-views': InboxViewsPredicateComparisonOptions,
    rules: RulesPredicateComparisonOptions,
    'rules-filters': RulesFiltersPredicateComparisonOptions,
    reporting: ReportingPredicateComparisonOptions,
    'ruleset-date-filters': RulesetDateFiltersPredicateComparisonOptions,
    'fin-sdr': FinSdrPredicateComparisonOptions
  },
  isTimeOnPage: equal('attribute', 'client_attributes.time_on_page'),
  isClientPageTargeting: equal('attribute', 'client_attributes.last_visited_url'),
  isPredicatePageTargeting: equal('attribute', 'last_visited_url'),
  isPageTarget: or('isPredicatePageTargeting', 'isClientPageTargeting'),
  isContentEventPredicate: equal('type', 'content_event'),
  isSeriesEvent: match('attribute', /^series/),
  /*
    We access this in editors/select-editor.hbs which is shared between child classes
    so we have defined it here to avoid having to override it in every child class
    We only override it in models/optional-select.js
  */
  predicateValue: alias('value'),
  initializeDefaultValue() {
    if (this.hasNoValue) {
      this.set('value', this.getDefaultValue());
    }
  },
  isClientPredicate: match('attribute', /^client_attributes./),
  isCompanyPredicate: match('attribute', /^company./),
  isUserEventPredicate: match('attribute', /^user_event/),
  userEventAttributeType: computed('attribute', 'isUserEventPredicate', function () {
    if (this.isUserEventPredicate) {
      return this.attribute.split('.').pop();
    }
  }),
  userEventAttributeTypeHumanName: computed('userEventAttributeType', function () {
    let userEventAttributeType = this.userEventAttributeType;
    switch (userEventAttributeType) {
      case undefined:
        return '';
      case 'first':
      case 'last':
        return `${userEventAttributeType} occurred`;
      default:
        return userEventAttributeType;
    }
  }),
  attributeIdentifier: computed('attribute', 'isUserEventPredicate', function () {
    let attribute = this.attribute;
    if (this.isUserEventPredicate) {
      return attribute.replace(new RegExp(`\.${this.userEventAttributeType}$`), '');
    }
    return attribute;
  }),
  getDefaultComparison(context = {}) {
    if (context.mode === 'goal' && this.goalComparison) {
      return this.goalComparison;
    } else {
      return this.defaultComparison;
    }
  },
  getComparisonOptions(context = {}) {
    let mode = context.mode || 'default';
    let options = this.optionsPerMode[mode];

    // If options are not found for the mode, fall back to default mode
    if (!options) {
      options = this.optionsPerMode['default'];
    }
    let attributeName = this.attribute;
    let genericType = this.genericType;

    // Check if attribute has backend-provided comparison operators
    let attributeInfo = this.info;
    if (attributeInfo) {
      // Handle both Ember Data models (with .get()) and plain objects (direct property access)
      let backendOperators = typeof attributeInfo.get === 'function' ? attributeInfo.get('comparisonOperators') : attributeInfo.comparisonOperators;
      if (backendOperators) {
        let backendOptions = mapBackendOperatorsToComparisonOptions(backendOperators, this.intl);
        if (backendOptions) {
          return backendOptions;
        }
      }
    }

    // Fall back to hardcoded options for backward compatibility
    if (options && options.custom && options.custom[attributeName] && options.custom[attributeName][genericType]) {
      return options.custom[attributeName][genericType].comparison;
    } else if (this.hasAttributeOptions) {
      return this.appService.app.canUseKnownComparisonForListPredicates ? options.attributeOptionSelectExtended.comparison : options.attributeOptionSelect.comparison;
    } else {
      return options[genericType].comparison;
    }
  },
  getDefaultValue(context = {}) {
    if (this.hasAttributeOptions) {
      return this.get('attributeOptions.firstObject.value');
    }
    return '';
  },
  changeComparison(comparison) {
    this.setProperties({
      comparison,
      value: this._initializeValueForComparison(comparison)
    });
  },
  changeValue(value) {
    this.set('value', value);
  },
  changeUserEventType(eventType) {
    let predicateType = eventType === 'count' ? 'user_event_integer' : 'user_event_date';
    this.setProperties({
      attribute: `${this.attributeIdentifier}.${eventType}`,
      type: predicateType
    });
  },
  toJSON() {
    return this.getProperties(['attribute', 'comparison', 'type', 'value']);
  },
  description: computed('attribute', 'comparison', 'type', 'value', function () {
    return this.getDescription().shortText;
  }),
  descriptionPrefix: computed('name', 'attributeIdentifier', function () {
    return this.name ? this.name.toLowerCase() : this.attributeIdentifier;
  }),
  descriptionWithName: computed('attribute', 'comparison', 'type', 'value', function () {
    return this.getDescription().fullText;
  }),
  goalDescription: computed('attribute', 'comparison', 'type', 'value', function () {
    return this.getGoalDescription();
  }),
  getDescription() {
    return this._getDescription(DefaultPredicateComparisonOptions, false, 'default');
  },
  getGoalDescription() {
    return this._getDescription(GoalPredicateComparisonOptions, true, 'goal');
  },
  _getDescription(customFilterMap, showNoEventPrefix, mode) {
    let descriptionPrefix = mode === 'goal' ? this.goalDescriptionPrefix : this.descriptionPrefix;
    let filterMap = {};
    filterMap[this.type] = customFilterMap[this.genericType];
    let comparisonAndValueDescription = FiltersToText.getPredicateDescription(this, filterMap, showNoEventPrefix);
    return {
      shortText: comparisonAndValueDescription,
      fullText: `${descriptionPrefix} ${comparisonAndValueDescription}`,
      icon: this.get('info.icon')
    };
  },
  goalDescriptionPrefix: ternaryToProperty('info.goalName', 'info.goalName', 'descriptionPrefix'),
  goalName: ternaryToProperty('info.goalName', 'info.goalName', 'name'),
  _initializeValueForComparison(comparison) {
    if (NULL_VALUE_COMPARISONS.includes(comparison)) {
      return null;
    } else if (this.hasNoValue) {
      return '';
    } else {
      return this.value;
    }
  }
}).reopenClass({
  genericTypes: ['anonymous', 'boolean', 'date', 'datetime', 'logical', 'numeric', 'numericSelect', 'urlVisit', 'optionalSelect', 'contentEvent', 'role', 'select', 'string', 'emailDisengagement', 'officeHoursSchedule', 'arraySize', 'surveyDropdownQuestion', 'teamCapacity', 'plan', 'companyAssociation', 'ticket_text_field', 'llmEvaluation'],
  anonymousTypes: ['anonymous'],
  booleanTypes: ['boolean', 'rules_filters_mark_priority', 'priority'],
  dateTypes: ['date', 'user_event_date'],
  datetimeTypes: ['datetime'],
  emailDisengagementTypes: ['email_disengagement'],
  arraySizeTypes: ['array_size'],
  logicalTypes: ['or', 'and'],
  numericSelectTypes: ['android_app_version', 'android_version', 'ios_app_version', 'ios_version', 'last_conversation_rating_given'],
  numericTypes: ['integer', 'float', 'user_event', 'user_event_integer', 'duration_integer', 'url_visit_integer'],
  urlVisitTypes: ['url_visit'],
  optionalSelectTypes: ['browser_locale', 'owner_id', 'admin_assignee_id', 'team_assignee_id', 'account_id', 'rules_filters_manual_tag', 'rules_filters_assign_to', 'rules_filters_apply_conversation_sla', 'list',
  // ✅ Add normalized type (supports both legacy and normalized)
  'conversation_attribute_list',
  // Keep for backward compatibility
  'attribute_descriptor_list', 'workflow_attribute_descriptor_list', 'created_by_id', 'ticket_type_id', 'whatsapp_integration_id', 'instagram_integration_id', 'ticket_state',
  // ✅ Already includes normalized type
  'custom_state_id',
  // Keep for backward compatibility
  'optional_manual_tag',
  // Keep for backward compatibility
  // Note: 'manual_tag' is in selectTypes for other use cases, but conversation.tag_ids
  // uses identifier-based conversion to 'optional_manual_tag' to get optional-select editor
  'channel', 'conversation_state', 'conversation_topic', 'conversation_started_by', 'sentiment', 'admin_id', 'brand_name', 'rating_requested_for_actor', 'guidance_applied'],
  roleTypes: ['role'],
  contentEventTypes: ['content_event'],
  selectTypes: ['tag', 'manual_tag', 'company_tag', 'android_device', 'ios_device', 'subscription_type', 'phone_country'],
  stringTypes: ['string', 'downcased_string', 'id', 'title_text_field'],
  officeHoursScheduleTypes: ['office_hours_schedule'],
  surveyDropdownQuestionTypes: ['survey_dropdown_question'],
  teamCapacityTypes: ['team_capacity'],
  planTypes: ['plan'],
  companyAssociationTypes: ['company_association'],
  llmEvaluationTypes: ['llm_evaluation'],
  getGenericType(type) {
    let genericType = Predicate.genericTypes.find(genericType => {
      let typesArray = Predicate[`${genericType}Types`];
      // Safety check: some genericTypes may not have a corresponding *Types array defined
      if (!typesArray) {
        return false;
      }
      return typesArray.includes(type);
    });
    if (!genericType) {
      throw new Error(`predicates/predicate: "${type}" is an unknown predicate type and does not map to any existing genericTypes.
      Make sure that either "${type}" is spelled correctly, or that it maps to an existing genericType in "lib/predicates/addon/models/predicate.js".
      If a genericType does not exist for "${type}" you will need to create a new predicate model.`);
    }
    return genericType;
  },
  createPredicateWithGenericType(store, predicate) {
    let type = Predicate.getGenericType(predicate.type);
    let normalizedPredicate = store.normalize(`predicates/${type}`, predicate).data.attributes;
    let modelClass = normalizedPredicate.subclass;
    if (modelClass === 'predicates/logical') {
      normalizedPredicate = Object.assign(normalizedPredicate, {
        predicates: normalizedPredicate.predicates.map(pred => Predicate.createPredicateWithGenericType(store, pred))
      });
    }
    return store.createFragment(modelClass, normalizedPredicate);
  },
  createPredicateWithDefaults(store, predicate, context) {
    let newPredicate = Predicate.createPredicateWithGenericType(store, predicate);
    if (!newPredicate.comparison) {
      newPredicate.changeComparison(newPredicate.getDefaultComparison(context));
    }
    if (!newPredicate.value) {
      newPredicate.changeValue(newPredicate.getDefaultValue({
        ...context,
        isCreatingPredicate: true
      }));
    }
    return newPredicate;
  },
  createCopyOfPredicate(store, predicate) {
    return store.createFragment(predicate.subclass, predicate.serialize());
  },
  *walkPredicates(predicates) {
    for (let predicate of predicates.toArray()) {
      yield predicate;
      let children = predicate.predicates;
      if (children) {
        yield* Predicate.walkPredicates(children);
      }
    }
  }
});